import React from 'react';
import {
  MDBCol,
  MDBRow,
  MDBInput,
  MDBModal,
  MDBModalFooter,
  MDBModalBody,
  MDBModalHeader,
  MDBDataTable,
  MDBContainer,
  MDBBtn,
} from 'mdbreact';
import './companies-view.css';
import Fab from '@material-ui/core/Fab';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import { Redirect } from "react-router-dom";
import SpinnerView from '../../components/spinner/spinner-view';
import Breadcrumb from '../../components/breadcrumb';

class CompanyView extends React.Component {

  state = {
    modal: false,
    wizardStep: 1,
    // Step 1
    companyName: '',
    // Step 2
    businessDescription: '',
    suggesting: false,
    suggestError: null,
    // Step 3
    suggestions: null,         // { entityTemplates, invoiceTemplate, appointmentTypes }
    enabledEntityTemplates: [], // array of booleans matching suggestions.entityTemplates
    enabledAppointmentTypes: [], // array of booleans matching suggestions.appointmentTypes
    refinement: '',
    refining: false,
    // Creating
    creating: false,
    // Table sort
    sortField: 'name',
    sortDir: 'asc',
    // Settings modal
    settingsCompany: null,
    deleteConfirm: '',
    deleting: false,
  }

  handleDeleteCompany = async () => {
    const { settingsCompany, deleteConfirm } = this.state;
    if (deleteConfirm !== settingsCompany.companyName) return;
    this.setState({ deleting: true });
    await this.props.dispatchDeleteCompany(settingsCompany._id);
    this.setState({ settingsCompany: null, deleteConfirm: '', deleting: false });
  }

  handleTableSort = (field) => {
    this.setState(prev => ({
      sortField: field,
      sortDir: prev.sortField === field && prev.sortDir === 'asc' ? 'desc' : 'asc',
    }));
  }

  toggle = () => {
    this.setState({
      modal: !this.state.modal,
      wizardStep: 1,
      companyName: '',
      businessDescription: '',
      suggesting: false,
      suggestError: null,
      suggestions: null,
      enabledEntityTemplates: [],
      enabledAppointmentTypes: [],
      refinement: '',
      refining: false,
      creating: false,
    });
  }

  // Step 1 → Step 2
  handleNextStep1 = () => {
    if (!this.state.companyName.trim()) return;
    this.setState({ wizardStep: 2, suggestError: null });
  }

  // Step 2: Generate templates from AI
  handleGenerate = async () => {
    const { businessDescription } = this.state;
    if (!businessDescription.trim()) return;

    this.setState({ suggesting: true, suggestError: null });
    try {
      const result = await this.props.dispatchSuggestTemplates(businessDescription.trim());
      if (result && result.entityTemplates) {
        this.setState({
          suggestions: result,
          enabledEntityTemplates: result.entityTemplates.map(() => true),
          enabledAppointmentTypes: result.appointmentTypes.map(() => true),
          wizardStep: 3,
          suggesting: false,
        });
      } else {
        this.setState({ suggestError: 'Could not generate templates. Please try again.', suggesting: false });
      }
    } catch (e) {
      this.setState({ suggestError: 'Error generating templates. Please try again.', suggesting: false });
    }
  }

  // Step 2: Skip AI setup, create with no templates
  handleSkip = () => {
    this.handleCreate(true);
  }

  // Step 3: Add refinement
  handleRefine = async () => {
    const { businessDescription, suggestions, refinement } = this.state;
    if (!refinement.trim()) return;

    this.setState({ refining: true });
    try {
      const result = await this.props.dispatchSuggestTemplates(
        businessDescription.trim(),
        suggestions,
        refinement.trim()
      );
      if (result && result.entityTemplates) {
        this.setState({
          suggestions: result,
          enabledEntityTemplates: result.entityTemplates.map(() => true),
          enabledAppointmentTypes: result.appointmentTypes.map(() => true),
          refinement: '',
          refining: false,
        });
      } else {
        this.setState({ refining: false });
      }
    } catch (e) {
      this.setState({ refining: false });
    }
  }

  // Step 3: Create company + selected templates
  handleCreate = async (skipTemplates = false) => {
    const { companyName, suggestions, enabledEntityTemplates, enabledAppointmentTypes } = this.state;

    const company = { companyName: companyName.trim() };
    let templates = null;

    if (!skipTemplates && suggestions) {
      templates = {
        entityTemplates: suggestions.entityTemplates.filter((_, i) => enabledEntityTemplates[i]),
        invoiceTemplate: suggestions.invoiceTemplate,
        appointmentTypes: suggestions.appointmentTypes.filter((_, i) => enabledAppointmentTypes[i]),
      };
    }

    this.setState({ creating: true });
    try {
      await this.props.dispatchCreateCompanyWithSetup(company, templates);
      this.setState({ modal: false, creating: false });
    } catch (e) {
      this.setState({ creating: false });
    }
  }

  toggleEntityTemplate = (index) => {
    const enabled = [...this.state.enabledEntityTemplates];
    enabled[index] = !enabled[index];
    this.setState({ enabledEntityTemplates: enabled });
  }

  toggleAppointmentType = (index) => {
    const enabled = [...this.state.enabledAppointmentTypes];
    enabled[index] = !enabled[index];
    this.setState({ enabledAppointmentTypes: enabled });
  }

  renderStep1() {
    const { companyName } = this.state;
    return (
      <>
        <MDBModalHeader toggle={this.toggle}>New Company</MDBModalHeader>
        <MDBModalBody>
          <MDBInput
            label="Company Name"
            value={companyName}
            onChange={e => this.setState({ companyName: e.target.value })}
            onKeyPress={e => e.key === 'Enter' && this.handleNextStep1()}
          />
        </MDBModalBody>
        <MDBModalFooter>
          <MDBBtn color="secondary" onClick={this.toggle}>Cancel</MDBBtn>
          <MDBBtn color="primary" onClick={this.handleNextStep1} disabled={!companyName.trim()}>
            Next
          </MDBBtn>
        </MDBModalFooter>
      </>
    );
  }

  renderStep2() {
    const { businessDescription, suggesting, suggestError, companyName } = this.state;
    return (
      <>
        <MDBModalHeader toggle={this.toggle}>
          Set Up "{companyName}"
        </MDBModalHeader>
        <MDBModalBody>
          <p className="text-muted mb-3">
            Describe your business and we'll suggest the right templates for you.
          </p>
          <MDBInput
            type="textarea"
            label="What type of business is this?"
            rows="3"
            value={businessDescription}
            onChange={e => this.setState({ businessDescription: e.target.value })}
            hint="e.g. veterinary practice that treats dogs and cats"
          />
          {suggestError && (
            <p className="text-danger mt-2">{suggestError}</p>
          )}
        </MDBModalBody>
        <MDBModalFooter>
          <MDBBtn color="secondary" onClick={() => this.setState({ wizardStep: 1 })}>Back</MDBBtn>
          <MDBBtn color="default" onClick={this.handleSkip} disabled={suggesting}>
            Skip
          </MDBBtn>
          <MDBBtn
            color="primary"
            onClick={this.handleGenerate}
            disabled={!businessDescription.trim() || suggesting}
          >
            {suggesting ? 'Generating...' : 'Generate'}
          </MDBBtn>
        </MDBModalFooter>
      </>
    );
  }

  renderStep3() {
    const {
      suggestions, enabledEntityTemplates, enabledAppointmentTypes,
      refinement, refining, creating, companyName
    } = this.state;

    if (!suggestions) return null;
    const { entityTemplates, invoiceTemplate, appointmentTypes } = suggestions;
    const hasEnabled = enabledEntityTemplates.some(Boolean) || enabledAppointmentTypes.some(Boolean);

    return (
      <>
        <MDBModalHeader toggle={this.toggle}>
          Review Templates for "{companyName}"
        </MDBModalHeader>
        <MDBModalBody style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          <p className="text-muted mb-3 small">
            Select which templates to create. You can always add more later.
          </p>

          {/* Entity Templates */}
          {entityTemplates.length > 0 && (
            <div className="mb-3">
              <h6 className="font-weight-bold">Client &amp; Entity Templates</h6>
              {entityTemplates.map((et, i) => (
                <div
                  key={i}
                  className={`d-flex align-items-start p-3 mb-2 rounded border ${enabledEntityTemplates[i] ? 'border-primary bg-light' : 'border-secondary'}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => this.toggleEntityTemplate(i)}
                >
                  <input
                    type="checkbox"
                    checked={enabledEntityTemplates[i]}
                    onChange={() => this.toggleEntityTemplate(i)}
                    className="mt-1 mr-3"
                    style={{ cursor: 'pointer' }}
                  />
                  <div>
                    <strong>{et.reviewName || et.name || `Template ${i + 1}`}</strong>
                    {et.secondary && <span className="badge badge-secondary ml-2">Secondary</span>}
                    <div className="text-muted small mt-1">
                      {et.reviewDescription || `${(et.descriptions || []).length} fields`}
                    </div>
                    <div className="text-muted small">
                      Fields: {(et.descriptions || []).map(d => d.description).join(', ')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Invoice Template */}
          {invoiceTemplate && (
            <div className="mb-3">
              <h6 className="font-weight-bold">Invoice Template</h6>
              <div className="d-flex align-items-center p-3 mb-2 rounded border border-success bg-light">
                <span className="text-success mr-3">✓</span>
                <div>
                  <strong>{invoiceTemplate.templateName || 'Standard Invoice'}</strong>
                  <div className="text-muted small mt-1">Always included — used to generate invoices</div>
                </div>
              </div>
            </div>
          )}

          {/* Appointment Types */}
          {appointmentTypes.length > 0 && (
            <div className="mb-3">
              <h6 className="font-weight-bold">Appointment Types</h6>
              {appointmentTypes.map((apt, i) => (
                <div
                  key={i}
                  className={`d-flex align-items-start p-3 mb-2 rounded border ${enabledAppointmentTypes[i] ? 'border-primary bg-light' : 'border-secondary'}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => this.toggleAppointmentType(i)}
                >
                  <input
                    type="checkbox"
                    checked={enabledAppointmentTypes[i]}
                    onChange={() => this.toggleAppointmentType(i)}
                    className="mt-1 mr-3"
                    style={{ cursor: 'pointer' }}
                  />
                  <div>
                    <strong>{apt.name}</strong>
                    <div className="text-muted small mt-1">
                      {apt.reviewDescription || apt.description || ''}
                    </div>
                    <div className="text-muted small">
                      Statuses: {(apt.statuses || []).map(s => s.name).join(', ')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Refinement */}
          <div className="mt-3 pt-3 border-top">
            <h6 className="font-weight-bold">Need something else?</h6>
            <div className="d-flex align-items-center">
              <MDBInput
                label="Describe what to add..."
                value={refinement}
                onChange={e => this.setState({ refinement: e.target.value })}
                className="flex-grow-1 mb-0"
                onKeyPress={e => e.key === 'Enter' && this.handleRefine()}
              />
              <MDBBtn
                color="default"
                size="sm"
                className="ml-2"
                onClick={this.handleRefine}
                disabled={!refinement.trim() || refining}
                style={{ marginTop: '-8px', whiteSpace: 'nowrap' }}
              >
                {refining ? '...' : 'Add'}
              </MDBBtn>
            </div>
          </div>
        </MDBModalBody>
        <MDBModalFooter>
          <MDBBtn color="secondary" onClick={() => this.setState({ wizardStep: 2 })}>Back</MDBBtn>
          <MDBBtn
            color="primary"
            onClick={() => this.handleCreate(false)}
            disabled={creating}
          >
            {creating ? 'Creating...' : `Create${hasEnabled ? ' with Templates' : ''}`}
          </MDBBtn>
        </MDBModalFooter>
      </>
    );
  }

  render() {
    if (this.props.user && !this.props.user._id) {
      return <Redirect to='/' />
    }

    const { sortField, sortDir } = this.state;

    const sortIcon = (field) => (
      <span style={{ marginLeft: 4, color: sortField === field ? '#333' : '#bbb' }}>
        {sortField === field ? (sortDir === 'asc' ? '↑' : '↓') : '⇅'}
      </span>
    );

    const colLabel = (label, field) => (
      <span
        style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
        onClick={() => this.handleTableSort(field)}
      >
        {label}{sortIcon(field)}
      </span>
    );

    var Company = [];

    if (this.props.companies && this.props.companies.length > 0) {
      const idToDate = (id) => {
        try {
          const ts = parseInt(String(id).substring(0, 8), 16) * 1000;
          return new Date(ts);
        } catch { return null; }
      };

      Company = this.props.companies.map(p => {
        const created = idToDate(p._id);
        return {
          name: p.companyName || '',
          created: created ? created.toLocaleDateString('en-ZA') : '',
          createdTs: created ? created.getTime() : 0,
          owner: p.ownerName || '',
          calendar: p.calendarCount || 0,
          invoices: p.invoiceCount || 0,
          view: <MDBContainer style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
            <span
              onClick={() => this.props.dispatchSelectCompany(p._id)}
              style={{ cursor: 'pointer', color: '#9e9e9e', display: 'inline-flex', alignItems: 'center', padding: '4px', borderRadius: '50%', transition: 'color 0.2s, background 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.color = '#f9a825'; e.currentTarget.style.background = '#fffde7'; }}
              onMouseLeave={e => { e.currentTarget.style.color = '#9e9e9e'; e.currentTarget.style.background = 'transparent'; }}
              title="Switch to this company"
            >
              <SwapHorizIcon style={{ fontSize: 18 }} />
            </span>
            <span
              onClick={() => this.setState({ settingsCompany: p, deleteConfirm: '' })}
              style={{ cursor: 'pointer', color: '#9e9e9e', display: 'inline-flex', alignItems: 'center', padding: '4px', borderRadius: '50%', transition: 'color 0.2s, background 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.color = '#c62828'; e.currentTarget.style.background = '#fdecea'; }}
              onMouseLeave={e => { e.currentTarget.style.color = '#9e9e9e'; e.currentTarget.style.background = 'transparent'; }}
              title="Delete Company"
            >
              <DeleteIcon style={{ fontSize: 18 }} />
            </span>
          </MDBContainer>,
        };
      });

      Company.sort((a, b) => {
        const av = sortField === 'created' ? a.createdTs : a[sortField];
        const bv = sortField === 'created' ? b.createdTs : b[sortField];
        if (typeof av === 'number') return sortDir === 'asc' ? av - bv : bv - av;
        return sortDir === 'asc'
          ? String(av).localeCompare(String(bv))
          : String(bv).localeCompare(String(av));
      });
    }

    const data = {
      columns: [
        { label: colLabel('Name', 'name'), field: 'name', sort: 'disabled' },
        { label: colLabel('Created', 'created'), field: 'created', sort: 'disabled' },
        { label: colLabel('Owner', 'owner'), field: 'owner', sort: 'disabled' },
        { label: colLabel('Calendar', 'calendar'), field: 'calendar', sort: 'disabled' },
        { label: colLabel('Invoices', 'invoices'), field: 'invoices', sort: 'disabled' },
        { label: '', field: 'view', sort: 'disabled' }
      ],
      rows: Company
    };

    const { wizardStep } = this.state;

    return (
      <div className="content-wrapper pl-5 pr-5">

        <Breadcrumb title={'Companies'} />

        <section className="content">
          <div className="container-fluid">

            <div className="companies-table-wrapper">
              <MDBDataTable
                striped
                bordered
                small
                data={data}
                entries={10}
                entriesOptions={[10, 25, 50, 100]}
                searching={true}
                pagesAmount={5}
                disableRetreatAfterSorting={true}
              />
            </div>

            <Fab onClick={this.toggle} className="fab primary-color" aria-label="add" style={{ position: 'fixed' }}>
              <AddIcon />
            </Fab>

            <MDBModal isOpen={this.state.modal} toggle={this.toggle} size="md">
              {wizardStep === 1 && this.renderStep1()}
              {wizardStep === 2 && this.renderStep2()}
              {wizardStep === 3 && this.renderStep3()}
            </MDBModal>

            {/* Company Settings Modal */}
            <MDBModal isOpen={!!this.state.settingsCompany} toggle={() => this.setState({ settingsCompany: null, deleteConfirm: '' })} size="md">
              {this.state.settingsCompany && (() => {
                const c = this.state.settingsCompany;
                const idToDate = (id) => { try { return new Date(parseInt(String(id).substring(0, 8), 16) * 1000).toLocaleDateString('en-ZA'); } catch { return ''; } };
                const canDelete = this.state.deleteConfirm === c.companyName;
                return (
                  <>
                    <MDBModalHeader toggle={() => this.setState({ settingsCompany: null, deleteConfirm: '' })}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <DeleteIcon style={{ fontSize: 20, color: '#c62828' }} />
                        {c.companyName}
                      </span>
                    </MDBModalHeader>
                    <MDBModalBody>
                      <p className="mb-1"><strong>Created:</strong> {idToDate(c._id)}</p>
                      <p className="mb-3"><strong>Owner:</strong> {c.ownerName || '—'}</p>

                      <div style={{ border: '1px solid #dc3545', borderRadius: 6, padding: '1rem', marginTop: '1.5rem' }}>
                        <p className="text-danger font-weight-bold mb-2">Danger Zone</p>
                        <p className="text-muted small mb-3">
                          This will permanently delete <strong>{c.companyName}</strong> and ALL its data — invoices, clients, appointments, templates, and more. This cannot be undone.
                        </p>
                        <MDBInput
                          label={`Type "${c.companyName}" to confirm`}
                          value={this.state.deleteConfirm}
                          onChange={e => this.setState({ deleteConfirm: e.target.value })}
                        />
                        <MDBBtn
                          color="danger"
                          onClick={this.handleDeleteCompany}
                          disabled={!canDelete || this.state.deleting}
                          style={{ width: '100%', marginTop: 8 }}
                        >
                          {this.state.deleting ? 'Deleting...' : 'Delete Company'}
                        </MDBBtn>
                      </div>
                    </MDBModalBody>
                    <MDBModalFooter>
                      <MDBBtn color="secondary" onClick={() => this.setState({ settingsCompany: null, deleteConfirm: '' })}>Close</MDBBtn>
                    </MDBModalFooter>
                  </>
                );
              })()}
            </MDBModal>

          </div>
        </section>
      </div>
    );
  }
}

export default CompanyView;
